<template>
  <v-card outlined class="mb-10 mx-2">
    <v-card-title :style="partnerColor.gradientStyle" class="title pt-4">
      <v-text-field
        dense
        v-model="search"
        append-icon="mdi-magnify"
        :label="$t('components.analytics.AnalyticsDataTableDialog.search')"
        single-line
        hide-details
        solo
      ></v-text-field>
      <v-spacer />
      <thg-quote-export-dialog
        :thgs="myThgs"
        :disableStatiSelector="true"
        :titleStyle="partnerColor.gradientStyle"
        titleTextStyle="color:white"
        :btnColor="partnerColor.primaryDarken_2"
      />
      <v-btn
        class="d-none d-sm-block ml-2"
        dense
        small
        depressed
        :color="partnerColor.primaryDarken_2"
        dark
        @click="newCar"
      >
        {{ $t("components.thg.ThgVehicleTable.btnText") }}
      </v-btn>
    </v-card-title>
    <v-divider />
    <v-data-table :headers="headers" :items="myThgs" :search="search" :items-per-page="10">
      <template v-slot:[`item.status`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip dark :color="thgStatus(item.status).color">
              <v-icon v-on="on" v-bind="attrs">
                {{ thgStatus(item.status).icon }}
              </v-icon>
            </v-chip>
          </template>
          <span> {{ $t(thgStatus(item.status).text) }}</span>
        </v-tooltip>
      </template>
      <template v-slot:[`item.registration.vehicleClass`]="{ item }">
        <v-tooltip bottom v-if="thgVehicleClass(item.registration?.vehicleClass)">
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-on="on" v-bind="attrs">
              {{ thgVehicleClass(item.registration?.vehicleClass)?.icon }}
            </v-icon>
          </template>
          <span> {{ $t(thgVehicleClass(item.registration?.vehicleClass)?.text || "") }}</span>
        </v-tooltip>
      </template>
      <template v-slot:[`footer.prepend`]>
        <thg-vehicle-table-legend-dialog />
      </template>
      <template v-slot:[`item.controls`]="{ item }">
        <span v-if="requestEmissionCalculationData(item.status)">
          <thg-emission-calculation-dialog :thg="item" />
        </span>
        <span v-else-if="item.decommissioningDate">
          <tooltip text="Fahrzeug wurde abgemeledet">
            <v-chip color="error">Fahrzeug Abgemeldet</v-chip>
          </tooltip>
        </span>
        <span v-else-if="isUpdateRegistrationImageActivated(item)">
          <thg-quote-update-my-registration-image-dialog :myThg="item" />
        </span>
        <span v-else-if="item.status == thgStatusEnum.CREATED">
          <thg-quote-vehicle-update-registration-dialog :selectedThg="item" :btn="true" />
        </span>
        <span
          v-else-if="isQuickCheckoutEnabled && item.quickCheckoutEnabledFor && item.quickCheckoutEnabledFor.length > 0"
        >
          <thg-quick-checkout :thg="item" @refresh="refresh" />
        </span>
      </template>
    </v-data-table>
  </v-card>
</template>

<script lang="ts">
import ThgQuoteExportDialog from "@/components/thg/ThgQuoteExportDialog.vue";
import ThgQuoteUpdateMyRegistrationImageDialog from "@/components/thg/ThgQuoteUpdateMyRegistrationImageDialog.vue";
import ThgQuoteVehicleUpdateRegistrationDialog from "@/components/thg/ThgQuoteVehicleUpdateRegistrationDialog.vue";
import ThgVehicleTableLegendDialog from "@/components/thg/ThgVehicleTableLegendDialog.vue";
import { IThgStatus } from "@/lib/interfaces/thg/thg-status.interface";
import { IThgVehicleClass } from "@/lib/interfaces/thg/thg-vehicle-class.interface";
import { PartnerColor } from "@/lib/partnerColor";
import { thgStatusMap } from "@/lib/utility/thgStatusMap";
import { thgVehicleClassMap } from "@/lib/utility/thgVehicleClassMap";
import DarkModeHighlightMixin from "@/mixins/DarkModeHighlightMixin.vue";
import { IThg } from "@/models/thg.entity";
import { ThgStatusEnum } from "@/store/enum/thg/thg-status.enum";
import { Component, Prop } from "vue-property-decorator";
import ThgQuickCheckout from "./ThgQuickCheckout.vue";
import Tooltip from "../utility/tooltip.vue";
import ThgEmissionCalculationDialog from "./ThgEmissionCalculationDialog.vue";
import { requestEmissionCalculationData } from "@/lib/thg/atStatiDataRequest";
import { ThgThgMeViewModelGen } from "@/services/thg/v1/data-contracts";

@Component({
  components: {
    Tooltip,
    ThgQuoteExportDialog,
    ThgQuoteVehicleUpdateRegistrationDialog,
    ThgQuoteUpdateMyRegistrationImageDialog,
    ThgVehicleTableLegendDialog,
    ThgQuickCheckout,
    ThgEmissionCalculationDialog
  }
})
export default class ThgVehicleTable extends DarkModeHighlightMixin {
  @Prop()
  myThgs!: ThgThgMeViewModelGen;

  thgStatusMap = thgStatusMap;
  thgVehicleClassMap = thgVehicleClassMap;
  thgStatusEnum = ThgStatusEnum;

  isLoading = false;

  search = "";

  partnerColor = new PartnerColor();

  requestEmissionCalculationData(thgStatusEnum: any): boolean {
    return requestEmissionCalculationData(thgStatusEnum);
  }

  thgStatus(thgStatusEnum: any): IThgStatus {
    return (
      this.thgStatusMap.get(thgStatusEnum) || {
        text: "components.thg.ThgStatusMap.UNDEFINED.text",
        textAlt: "components.thg.ThgStatusMap.UNDEFINED.textAlt",
        icon: "mdi-help",
        portalText: "components.thg.ThgStatusMap.UNDEFINED.portalText",
        color: "error"
      }
    );
  }

  thgVehicleClass(thgVehicleClass: any): IThgVehicleClass | undefined {
    return this.thgVehicleClassMap.get(thgVehicleClass);
  }

  newCar() {
    this.$router.push({ path: "/thghome" });
  }

  get headers() {
    return [
      { text: this.$t("components.thg.ThgVehicleTable.table.status"), value: "status" },
      { text: this.$t("components.thg.ThgVehicleTable.table.year"), value: "year" },
      { text: this.$t("components.thg.ThgVehicleTable.table.numberplate"), value: "numberplate" },
      {
        text: this.$t("components.thg.ThgVehicleTable.table.vin"),
        value: "registration.identificationnumber"
      },
      { text: this.$t("components.thg.ThgVehicleTable.table.firstName"), value: "registration.firstname" },
      { text: this.$t("components.thg.ThgVehicleTable.table.lastName"), value: "registration.name" },
      { text: this.$t("components.thg.ThgVehicleTable.table.vehicleClass"), value: "registration.vehicleClass" },
      {
        text: "",
        value: "controls"
      }
    ];
  }

  get isQuickCheckoutEnabled() {
    return true;
  }

  isUpdateRegistrationImageActivated(item: IThg) {
    return (
      item.status == ThgStatusEnum.REQUEST_REGISTRATION_IMAGES ||
      item.status == ThgStatusEnum.REGISTRATION_IMAGES_REQUESTED
    );
  }

  refresh() {
    this.$emit("refresh");
  }
}
</script>
